import { useEffect, useState, useMemo, useRef } from "react";
import { supabase } from "./supabase";
import { useIngredientInfo } from "./useIngredientInfo";
import { normalizeUnitId } from "./units/aliases";

// Observation-learned package-size chip source with AI-generated
// fallback, replacing the old admin-curated
// `ingredient_info.packaging.sizes` path.
//
// Three tiers of chips, stacked in priority order:
//
//   Tier 1 — brand + canonical observations. `popular_package_sizes`
//     RPC (migration 0063) aggregates `pantry_items.max + unit + brand`
//     across every household. "Kerrygold Butter" → 8oz × 4x, 1lb × 1x.
//
//   Tier 2 — canonical-wide observations. Same RPC with brand=null.
//     "butter" across every brand → 8oz, 1lb, 2lb, ...
//
//   Tier 3 — AI-generated typical sizes from `ingredient_info.info
//     .package.typicalSizes`. Claude's enrichment prompt always asks
//     for this array (supabase/functions/enrich-ingredient:131), so
//     every enriched canonical carries suggestions like
//     ["16 oz", "1 lb bag", "1 gallon jug"]. Parsed client-side into
//     {amount, unit} chip objects. Covers the cold-start case: the
//     moment a canonical is enriched (auto-fires on creation),
//     users get plausible sizes without waiting for household
//     observations to accumulate.
//
// Brand-specific results are marked with `brand`; AI-generated chips
// carry `source: "ai"` so the UI can differentiate if needed.
// Deduplicated across tiers on (amount, unit), so an 8oz from
// observations absorbs the AI's 8oz suggestion into a single chip.

const CACHE_TTL_MS = 60_000; // one-minute in-memory cache
const cache = new Map();     // key: `${brand}|${canonical}|${limit}` → { ts, data }

function cacheKey(brand, canonical, limit) {
  return `${brand || ""}|${canonical || ""}|${limit}`;
}

// Flush the in-memory cache for a specific (brand, canonical) pair
// so the very next call re-queries Supabase. Called from the
// Shop Mode commit path: when a user just taught us a new package
// size by inserting a pantry_items row, the 60s cache TTL would
// hide that observation from the user's next scan. After commit we
// clear the cache so the chip they JUST taught shows up on their
// next visit. Both brand-specific and canonical-only entries are
// flushed because the canonical-wide rollup (Tier 2) also sees the
// new row.
export function clearPopularPackagesCache(brand, canonicalId) {
  if (!canonicalId) return;
  const brandKey = brand || "";
  for (const key of Array.from(cache.keys())) {
    // key shape: `${brand}|${canonical}|${limit}`
    const [b, c] = key.split("|");
    if (c !== canonicalId) continue;
    // Flush both the brand-specific entry AND the canonical-wide
    // (brand="") entry — the new row affects both tiers.
    if (b === brandKey || b === "") cache.delete(key);
  }
}

// Container tokens Claude sometimes appends to typical-size strings
// ("1 lb bag", "1 gallon jug"). The leading number+unit is the real
// measurement; the trailing container word is just the package shape
// — orthogonal to pantry_items.max. Stripped during parse because
// "bag" as a unit is tautological (the row IS the bag).
// Container tokens — single-word base forms. Multi-word containers
// like "plastic bag", "foil wrapper", "stand-up pouch" are matched
// via CONTAINER_PHRASES below before falling back to single-token
// matching here.
const CONTAINER_TOKENS = new Set([
  "bag", "bottle", "jar", "can", "jug", "box", "carton", "tub",
  "package", "pack", "packet", "pouch", "tin", "crate", "bin",
  "sleeve", "shaker", "container", "bucket", "wrapper", "tray",
  "canister", "cylinder", "tube", "brick", "block",
]);

// Multi-word container phrases. Order matters — longer phrases first
// so "vacuum-sealed package" wins over "package", "plastic bag" wins
// over "bag", etc. Phrases stored as arrays of lowercase tokens.
const CONTAINER_PHRASES = [
  ["vacuum", "sealed", "package"],
  ["vacuum", "sealed", "tray"],
  ["vacuum", "sealed", "bag"],
  ["stand", "up", "pouch"],
  ["resealable", "bag"],
  ["resealable", "pouch"],
  ["plastic", "bag"],
  ["paper", "bag"],
  ["foil", "bag"],
  ["mesh", "bag"],
  ["plastic", "bottle"],
  ["glass", "bottle"],
  ["squeeze", "bottle"],
  ["spray", "bottle"],
  ["aluminum", "can"],
  ["steel", "can"],
  ["cardboard", "canister"],
  ["paperboard", "box"],
  ["corrugated", "box"],
  ["waxed", "carton"],
  ["tetra", "pak"],
  ["glass", "jar"],
  ["plastic", "jar"],
  ["plastic", "tray"],
  ["foam", "tray"],
  ["plastic", "tub"],
  ["paper", "carton"],
  ["individual", "wrapper"],
  ["foil", "wrapper"],
  ["plastic", "wrap"],
  ["frozen", "brick"],
  ["frozen", "pouch"],
];

// Try to match the longest container phrase starting at `tokens[i]`.
// Returns { phrase: "plastic bag", consumed: 2 } or null.
function matchContainerPhrase(tokens, i) {
  for (const phrase of CONTAINER_PHRASES) {
    if (i + phrase.length > tokens.length) continue;
    let ok = true;
    for (let j = 0; j < phrase.length; j++) {
      if (tokens[i + j] !== phrase[j]) { ok = false; break; }
    }
    if (ok) return { phrase: phrase.join(" "), consumed: phrase.length };
  }
  return null;
}

// Map Claude's natural-language unit words to our registry's
// canonical unit ids. Partial coverage — anything not in this map
// passes through verbatim (so "tbsp" stays "tbsp", which matches
// the registry).
const UNIT_NORMALIZE = {
  ounce: "oz",    ounces: "oz",
  pound: "lb",    pounds: "lb",  lbs: "lb",
  gram: "g",      grams: "g",
  kilogram: "kg", kilograms: "kg",
  gallons: "gallon",
  quarts: "quart",
  pints: "pint",
  cups: "cup",
  liters: "l",    liter: "l",    litres: "l",
  milliliters: "ml", ml: "ml",
};

// Parse one Claude typical-size string into a structured record.
// Handles:
//   "16 oz"            → {amount: 16,  unit: "oz"}
//   "1 lb bag"         → {amount: 1,   unit: "lb",   container: "bag"}
//   "1 gallon jug"     → {amount: 1,   unit: "gallon", container: "jug"}
//   "8 fl oz"          → {amount: 8,   unit: "fl_oz"}
//   "8 ct (1 lb)"      → {amount: 8,   unit: "count", countLabel: "ct", secondary: {amount: 1, unit: "lb"}}
//   "4 chops"          → {amount: 4,   unit: "count", countLabel: "chops"}
//   "12 oz (~12 strips)" → {amount: 12, unit: "oz", secondary: {amount: 12, unit: "count"}}
//   "13 oz party size" → {amount: 13,  unit: "oz",   marketingTier: "party size"}
//   "10 oz family size" → {amount: 10, unit: "oz",   marketingTier: "family size"}
// Returns null for anything that doesn't start with a number.
//
// Returned shape:
//   amount         — primary numeric quantity (the "8" in "8 ct")
//   unit           — primary unit ("count", "lb", "fl_oz", etc.)
//   countLabel     — DISPLAY-ONLY: the original count noun ("chops",
//                    "links") when the unit normalized to "count", so
//                    pills can read "4 chops" instead of "4 count".
//                    Storage always keeps unit: "count".
//   container      — optional packaging container if present in the
//                    string ("bag", "jar", "bottle", "can", …) so the
//                    UI can display "8 oz bag" → "🍫 8 oz · bag"
//   secondary      — optional {amount, unit} extracted from a
//                    parenthetical ("(1 lb)" → secondary mass for
//                    nutrition math when primary is count-based)
//   marketingTier  — optional shelf qualifier ("family size", "party
//                    size", "single-serve") — surfaces as a chip
//                    qualifier without polluting the unit field
function parseTypicalSize(str) {
  if (typeof str !== "string") return null;
  const cleaned = str.trim().toLowerCase();
  const m = cleaned.match(/^(\d+(?:\.\d+)?)\s+(.+)$/);
  if (!m) return null;
  const amount = parseFloat(m[1]);
  if (!Number.isFinite(amount) || amount <= 0) return null;

  let rest = m[2];

  // Extract parenthetical secondary unit if present, e.g. "8 ct (1 lb)"
  // → primary "8 ct", secondary "1 lb". Tilde / ~ prefix ("(~12 strips)")
  // means approximate; we record it but don't flag — the user can see
  // the original on the chip.
  let secondary = null;
  const parenMatch = rest.match(/\(\s*~?\s*(\d+(?:\.\d+)?)\s*([a-z_]+)\s*\)/);
  if (parenMatch) {
    const sa = parseFloat(parenMatch[1]);
    const su = parenMatch[2];
    if (Number.isFinite(sa) && sa > 0 && su) {
      // Same fallback order as the primary unit below: local map,
      // then the global alias map ("strips" → "count"), then verbatim.
      secondary = { amount: sa, unit: UNIT_NORMALIZE[su] || normalizeUnitId(su) || su };
    }
    // Remove the parenthetical from `rest` so it doesn't leak into
    // container extraction below.
    rest = rest.replace(parenMatch[0], " ").trim();
  }

  // Extract a marketing tier ("family size", "party size",
  // "single-serve", "lunch size") and strip it from rest.
  const MARKETING_TIERS = [
    "family size", "party size", "single serve", "single-serve",
    "lunch size", "snack size", "fun size", "share size",
    "value pack", "bulk pack",
  ];
  let marketingTier = null;
  for (const t of MARKETING_TIERS) {
    if (rest.includes(t)) {
      marketingTier = t;
      rest = rest.replace(t, " ").trim();
      break;
    }
  }

  const tokens = rest.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return null;

  // Find the unit (first non-container token), and capture the
  // container word(s) if present. For two-word units (fl oz, half
  // gallon) we assemble them, then anything left over is container.
  let unit;
  let unitConsumed = 1;
  let countLabel = null;
  if (tokens.length >= 2 && (tokens[0] === "fl" || tokens[0] === "fluid") && tokens[1].startsWith("oz")) {
    unit = "fl_oz";
    unitConsumed = 2;
  } else if (tokens.length >= 2 && tokens[0] === "half" && tokens[1] === "gallon") {
    unit = "half_gallon";
    unitConsumed = 2;
  } else {
    const raw = tokens[0];
    // Local map first, then the global alias map so count NOUNS
    // ("4 chops", "10 links") and short forms ("8 ct") normalize to
    // the storage id "count". Verbatim pass-through stays the final
    // fallback so registry-matching units like "tbsp" survive.
    const aliased = UNIT_NORMALIZE[raw] || normalizeUnitId(raw) || null;
    unit = aliased || raw;
    // Carry the original noun for display ("4 chops", not "4 count").
    // Display-only — storage keeps unit: "count".
    if (aliased === "count" && raw !== "count") countLabel = raw;
    unitConsumed = 1;
  }
  // Remaining tokens are candidate container words. Walk the leftover
  // looking for multi-word container phrases first ("plastic bag",
  // "vacuum-sealed package") — the LONGEST match wins so a granular
  // container like "stand-up pouch" rates above the bare "pouch".
  // Falls through to single-token CONTAINER_TOKENS matching afterward.
  const tail = tokens.slice(unitConsumed);
  let container = null;
  for (let i = 0; i < tail.length; i++) {
    const phrase = matchContainerPhrase(tail, i);
    if (phrase) { container = phrase.phrase; break; }
    if (CONTAINER_TOKENS.has(tail[i])) { container = tail[i]; break; }
  }

  return {
    amount,
    unit,
    ...(countLabel ? { countLabel } : {}),
    ...(container ? { container } : {}),
    ...(secondary ? { secondary } : {}),
    ...(marketingTier ? { marketingTier } : {}),
  };
}

async function fetchPopular(brand, canonical, limit) {
  if (!canonical) return [];
  const key = cacheKey(brand, canonical, limit);
  const hit = cache.get(key);
  if (hit && Date.now() - hit.ts < CACHE_TTL_MS) return hit.data;

  const { data, error } = await supabase.rpc("popular_package_sizes", {
    p_brand: brand || null,
    p_canonical: canonical,
    p_limit: limit,
  });
  if (error) {
    // Function missing (migration 0063 not applied) or RLS misconfigure.
    // Non-fatal — the UI falls back to "no chips, user types their size"
    // which is still the happy path for the first user of any canonical.
    console.warn("[popular_package_sizes] rpc failed:", error.message);
    return [];
  }
  const rows = (data || []).map(r => ({
    amount: Number(r.amount),
    unit: r.unit,
    brand: r.brand || null,
    n: Number(r.n || 0),
  }));
  cache.set(key, { ts: Date.now(), data: rows });
  return rows;
}

// Async imperative variant of the hook for non-React callers — the
// Scanner's barcode onDetected needs to auto-apply a learned package
// size BEFORE building the scan row, so the normal hook (runs during
// render) is too late. Same fetch/cache path; returns the raw rows
// so the caller can pick the top non-count entry.
export async function fetchPopularPackages(brand, canonicalId, limit = 5) {
  if (!canonicalId) return [];
  const [specific, generic] = await Promise.all([
    brand ? fetchPopular(brand, canonicalId, limit) : Promise.resolve([]),
    fetchPopular(null, canonicalId, limit),
  ]);
  const seen = new Set();
  const out = [];
  const push = (entry) => {
    if (out.length >= limit) return;
    const sig = `${entry.amount}|${entry.unit}`;
    if (seen.has(sig)) return;
    seen.add(sig);
    out.push(entry);
  };
  specific.forEach(push);
  generic.forEach(push);
  return out;
}

/**
 * Returns an array of up to `limit` popular package sizes for the
 * (brand, canonical) pair. Each entry:
 *
 *   { amount, unit, brand, n }
 *
 *   amount — the package size (becomes pantry_items.max)
 *   unit   — the canonical unit
 *   brand  — the brand the observations came from (null for
 *            canonical-wide fallback hits)
 *   n      — observation count, for sorting / optional display
 *
 * Brand-specific results appear first; canonical-wide fills the
 * remainder. De-duplicated across the two tiers so a 16oz Barilla
 * hit doesn't also surface as a 16oz canonical-wide hit.
 *
 * Accepts null brand (returns canonical-wide only) and null canonical
 * (returns empty — we don't recommend without a canonical anchor).
 */
export function usePopularPackages(brand, canonicalId, limit = 5) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const latestKey = useRef("");
  // Pull ingredient_info from context so Tier 3 (AI-generated typical
  // sizes) has data without a second round-trip. The IngredientInfo
  // provider is already mounted at the app root, so getInfo() is
  // cached and synchronous.
  const { getInfo } = useIngredientInfo();

  useEffect(() => {
    const key = cacheKey(brand, canonicalId, limit);
    latestKey.current = key;
    if (!canonicalId) { setRows([]); return; }
    setLoading(true);
    let alive = true;
    (async () => {
      // Tiers 1 + 2 fetched in parallel. Brand-specific first;
      // canonical-wide fills what remains. If brand is null, skip
      // the specific tier — the generic fetch already covers it.
      const [specific, generic] = await Promise.all([
        brand ? fetchPopular(brand, canonicalId, limit) : Promise.resolve([]),
        fetchPopular(null, canonicalId, limit),
      ]);
      // A stale request (user switched brand mid-flight) shouldn't
      // clobber the current view — guard on the key we captured at
      // effect start.
      if (!alive || latestKey.current !== key) return;

      // Merge all three tiers. De-dup across them on (amount, unit)
      // so "16oz Barilla" from tier 1 absorbs "16oz null" from tier 2
      // or "16 oz" from tier 3 into one chip.
      const seen = new Set();
      const out = [];
      const push = (entry) => {
        if (out.length >= limit) return;
        const sig = `${entry.amount}|${entry.unit}`;
        if (seen.has(sig)) return;
        seen.add(sig);
        out.push(entry);
      };
      for (const r of specific) push(r);
      for (const r of generic)  push(r);

      // Tier 3 — AI-generated typicalSizes from the ingredient_info
      // enrichment. Parsed client-side, marked source:"ai" so the UI
      // can style them differently if desired. Only fills remaining
      // slots after observation tiers.
      if (out.length < limit) {
        const info = getInfo(canonicalId);
        const typical = Array.isArray(info?.package?.typicalSizes)
          ? info.package.typicalSizes
          : [];
        for (const str of typical) {
          const parsed = parseTypicalSize(str);
          if (!parsed) continue;
          push({
            amount: parsed.amount,
            unit: parsed.unit,
            brand: null,
            n: 0,
            source: "ai",
            // Display-only noun ("chops") so count pills read
            // "4 chops" instead of "4 count". Storage stays "count".
            ...(parsed.countLabel ? { countLabel: parsed.countLabel } : {}),
          });
          if (out.length >= limit) break;
        }
      }

      setRows(out);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [brand, canonicalId, limit, getInfo]);

  return useMemo(() => ({ rows, loading }), [rows, loading]);
}
